import React, {
	useEffect,
	useId,
	useMemo,
	type ReactElement,
	// @ts-expect-error TS-2305, hidden symbol. Used for debugging
	__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED,
} from 'react';
import { OWNER_PREFIX } from '@jira/platform__ownership-marker/src/ui/extractors.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';

type MountTrackerRecord = {
	renderCount: number;
	state: 'rendered' | 'commited' | 'unmounted';
};

type ReactFiber = {
	elementType: unknown;
	return: ReactFiber | null;
};

let mountTracker: Record<string, MountTrackerRecord & { fiberNode: ReactFiber | null }> = {};
autoBeforeEachTest(() => {
	mountTracker = {};
});

const extractOwnershipStack = (fiberNode: ReactFiber | null | undefined): string[] => {
	const stack: string[] = [];
	let currentNode = fiberNode;
	// oxlint-disable-next-line no-cond-assign
	while ((currentNode = currentNode?.return)) {
		if (typeof currentNode.elementType === 'function') {
			const componentName = currentNode.elementType.name || 'unnamed';
			if (componentName.startsWith(OWNER_PREFIX)) {
				stack.push(componentName);
			}
		}
	}
	return stack.reverse();
};

export const getPlaceholderMountTracker = (): Record<
	string,
	MountTrackerRecord & { stack: string[] }
> => {
	return Object.fromEntries(
		Object.entries(mountTracker).map(([key, { fiberNode, ...value }]) => {
			return [
				key,
				{
					...value,
					stack: extractOwnershipStack(fiberNode),
				},
			];
		}),
	);
};

export const getCurrentComponent = (): ReactFiber | null => {
	// Get the current owner
	const { ReactCurrentOwner } = __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
	const currentOwner = ReactCurrentOwner.current;

	if (currentOwner && currentOwner.elementType) {
		return currentOwner;
	}
	return null;
};

export const ReportMountAttempt = ({ id }: { id: string }): null => {
	useMemo(() => {
		// eslint-disable-next-line react-hooks/immutability
		mountTracker[id] = mountTracker[id] ?? { renderCount: 0, state: 'rendered', fiberNode: null };
		if (!__SERVER__) {
			mountTracker[id].fiberNode = getCurrentComponent();
			mountTracker[id].renderCount++;
		}
	}, [id]);

	useEffect(() => {
		mountTracker[id].state = 'commited';
		return () => {
			mountTracker[id].state = 'unmounted';
		};
	}, [id]);
	return null;
};

/**
 * @internal
 */
export const useMountId = (): string => useId();

const useMountTrackerInternal = (name: string): ReactElement | null => {
	const id = useMountId();
	return useMemo(() => <ReportMountAttempt id={`${name}::${id}`} />, [name, id]);
};

export const useMountTracker: (name: string) => ReactElement | null = functionWithCondition(
	() => fg('jfp-magma-track-placeholder-mounts'),
	useMountTrackerInternal,
	() => null,
);
